<template>
  <div class="form-group tree-dropdown">
    <label v-if="label" class="form-label">{{ label }}</label>
    <VDropdown
      :triggers="['click']"
      placement="bottom-start"
      :auto-hide="true"
      @show="onShow"
      @hide="onHide"
    >
      <button
        type="button"
        class="form-control text-start tree-dropdown-trigger"
      >
        <span
          class="tree-dropdown-trigger-label"
          :class="{ placeholder: !selectedLabel }"
        >
          {{ selectedLabel || "Select..." }}
        </span>
        <i class="fa-solid fa-caret-down tree-dropdown-caret"></i>
      </button>
      <template #popper="{ hide }">
        <div class="tree-dropdown-menu">
          <input
            ref="searchInput"
            type="text"
            class="form-control tree-dropdown-search"
            placeholder="Search..."
            v-model="query"
          />
          <div class="tree-dropdown-tree">
            <Tree :value="filteredItems" :auto-open-all="true">
              <template #default="{ node }">
                <span
                  class="tree-dropdown-item"
                  :class="{ active: isSelected(node) }"
                  @click="select(node, hide)"
                >
                  <i
                    class="fa-solid me-1"
                    :class="
                      hasChildren(node)
                        ? 'fa-folder folder-icon'
                        : 'fa-file'
                    "
                  ></i>
                  {{ node.label }}
                </span>
              </template>
            </Tree>
            <div
              v-if="filteredItems.length === 0"
              class="tree-dropdown-empty text-muted"
            >
              No matches
            </div>
          </div>
        </div>
      </template>
    </VDropdown>
  </div>
</template>

<script>
import Tree from "@/components/common/tree/Tree.vue";

const findLabel = (nodes, id) => {
  if (!Array.isArray(nodes)) return null;
  for (const node of nodes) {
    if (node.id === id) return node.label;
    if (Array.isArray(node.children)) {
      const inner = findLabel(node.children, id);
      if (inner !== null) return inner;
    }
  }
  return null;
};

const filterTree = (nodes, lowerQuery) => {
  if (!Array.isArray(nodes)) return [];
  const result = [];
  for (const node of nodes) {
    const matchesSelf =
      typeof node.label === "string" &&
      node.label.toLowerCase().includes(lowerQuery);
    const filteredChildren = filterTree(node.children, lowerQuery);
    if (matchesSelf || filteredChildren.length > 0) {
      result.push({
        ...node,
        children: filteredChildren.length > 0 ? filteredChildren : node.children
      });
    }
  }
  return result;
};

export default {
  components: { Tree },
  props: {
    label: { type: String, required: false, default: "" },
    modelValue: { required: true },
    items: { type: Array, required: true }
  },
  emits: ["update:modelValue"],
  data() {
    return { query: "" };
  },
  computed: {
    selectedLabel() {
      if (this.modelValue === undefined || this.modelValue === null) return "";
      return findLabel(this.items, this.modelValue) ?? "";
    },
    filteredItems() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.items;
      return filterTree(this.items, q);
    }
  },
  methods: {
    hasChildren(node) {
      return Array.isArray(node.children) && node.children.length > 0;
    },
    isSelected(node) {
      return node.id === this.modelValue;
    },
    select(node, hide) {
      this.$emit("update:modelValue", node.id);
      hide();
    },
    onShow() {
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
      });
    },
    onHide() {
      this.query = "";
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/_css/variables.scss";

.tree-dropdown-trigger {
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  background-color: white;

  .tree-dropdown-trigger-label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.placeholder {
      color: $gray-500;
    }
  }

  .tree-dropdown-caret {
    margin-left: 0.5rem;
    flex-shrink: 0;
    opacity: 0.6;
  }
}

.tree-dropdown-menu {
  min-width: 220px;
  max-width: 360px;
  padding: 0.5rem;
}

.tree-dropdown-search {
  margin-bottom: 0.5rem;
}

.tree-dropdown-tree {
  max-height: 260px;
  overflow-y: auto;
}

.tree-dropdown-item {
  cursor: pointer;
  display: inline-block;
  padding: 2px 4px;
  border-radius: $border-radius;

  &:hover {
    background-color: $gray-200;
  }

  &.active {
    background-color: $brand-100;
    font-weight: 600;
  }
}

.folder-icon {
  color: $brand-600;
}

.tree-dropdown-empty {
  padding: 0.5rem;
  font-size: 0.875rem;
  text-align: center;
}
</style>
